import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { BackButton, Button } from '../components/common';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { DB_DEFAULTS } from '../utils/constants';

const CreateClub = ({ currentUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    description: '',
    strength_rating: DB_DEFAULTS.CLUB_STRENGTH_RATING
  });

  // Check if user is super-admin
  const isSuperAdmin = currentUser?.role === 'super_admin';
  
  // Debug logging
  console.log('CreateClub Debug:', {
    currentUser: currentUser ? {
      id: currentUser.id,
      role: currentUser.role,
      email: currentUser.email
    } : null,
    isSuperAdmin
  });
  
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    if (!isSuperAdmin) {
      setError('Only super-admins can create clubs. Please contact your administrator.');
    }
  }, [currentUser, navigate, isSuperAdmin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate form
      if (!formData.name.trim()) {
        throw new Error('Club name is required');
      }

      // Create the club
      const { data: club, error: clubError } = await supabase
        .from('clubs')
        .insert([{
          name: formData.name.trim(),
          location: formData.location.trim() || null,
          description: formData.description.trim() || null,
          strength_rating: parseInt(formData.strength_rating) || DB_DEFAULTS.CLUB_STRENGTH_RATING
        }])
        .select()
        .single();

      if (clubError) throw clubError;

      // Create the first season for the club
      const { data: season, error: seasonError } = await supabase
        .from('seasons')
        .insert([{
          club_id: club.id,
          season_number: 1,
          name: 'Season 1',
          created_by: currentUser.id,
          season_status: 'current'
        }])
        .select()
        .single();

      if (seasonError) throw seasonError;

      // Add the current user as club owner
      const { error: membershipError } = await supabase
        .from('players_club')
        .insert([{
          player_id: currentUser.id,
          club_id: club.id,
          club_role: 'admin',
          is_active: true
        }]);

      if (membershipError) throw membershipError;

      setSuccess('Club created successfully! Redirecting to club admin...');
      
      // Redirect to club admin after a short delay
      setTimeout(() => {
        navigate('/club-admin');
      }, 2000);

    } catch (err) {
      console.error('Error creating club:', err);
      setError(err.message || 'Failed to create club');
    } finally {
      setLoading(false);
    }
  };

  if (error && !isSuperAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-8">
        <Card variant="danger" size="large">
          <Heading level={3} className="text-text-primary mb-4">Access Denied</Heading>
          <Text className="text-text-secondary mb-6">{error}</Text>
          <Button
            onClick={() => navigate('/')}
            variant="danger"
          >
            Return to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <Card variant="default" size="xlarge">
        <div className="text-center mb-8">
          <div className="text-6xl mb-6">🏸</div>
          <Heading level={1} className="text-display text-text-primary mb-6">Create Your First Club</Heading>
          <Text className="text-body-large text-text-secondary">
            Set up your badminton club to start managing sessions and players
          </Text>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Club Name */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-text-primary">
              Club Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Central Badminton League"
              className="w-full border border-border-medium rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface shadow-sm transition-all duration-200 hover:border-border"
              required
            />
          </div>

          {/* Location */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-text-primary">
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="e.g., London, UK"
              className="w-full border border-border-medium rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface shadow-sm transition-all duration-200 hover:border-border"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-text-primary">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Tell us about your club..."
              rows={3}
              className="w-full border border-border-medium rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface shadow-sm transition-all duration-200 hover:border-border resize-none"
            />
          </div>

          {/* Strength Rating */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-text-primary">
              Default ELO Rating
            </label>
            <input
              type="number"
              name="strength_rating"
              value={formData.strength_rating}
              onChange={handleInputChange}
              placeholder="1200"
              min="800"
              max="2000"
              className="w-full border border-border-medium rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-surface shadow-sm transition-all duration-200 hover:border-border"
            />
            <Typography variant="muted" className="text-xs">
              This will be the starting ELO rating for new players joining your club
            </Typography>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <Button
              type="submit"
              disabled={loading}
              variant="primary"
              size="large"
              className="w-full"
            >
              {loading ? 'Creating Club...' : 'Create Club'}
            </Button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <Card variant="danger" size="small">
              <Typography variant="body" className="text-danger-dark">{error}</Typography>
            </Card>
          )}
          {success && (
            <Card variant="success" size="small">
              <Typography variant="body" className="text-success-dark">{success}</Typography>
            </Card>
          )}
        </form>

        {/* Back Button */}
        <div className="mt-8">
          <BackButton onClick={() => navigate('/')}>
            Back to Home
          </BackButton>
        </div>
      </Card>
    </div>
  );
};

export default CreateClub;
